import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';
import { useDatadog } from '#src/components/Datadog/Datadog.tsx';

export const TanstackQueryProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { addError } = useDatadog();

  const queryClient = useMemo(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error, query) => {
            addError(`Query failed: ${error.message}`, {
              error,
              queryKey: query.options.queryKey,
            });
          },
        }),
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => {
            addError(`Mutation failed: ${error.message}`, {
              error,
              mutationKey: mutation.options.mutationKey,
            });
          },
        }),
        defaultOptions: {
          queries: {
            // For re-fetching below
            staleTime: 15 * 60 * 1_000, // 15 minutes

            // This will cause queries that a component has to refetch whenever the page is navigated to, even if not stale.
            // Note that if we have a (global) provider, that will stay mounted and won't be subject to this.
            refetchOnMount: 'always',

            // These apply to when data is stale
            refetchOnReconnect: true,
            refetchOnWindowFocus: true,
            // By default, don't refetch on an interval even if stale per definition below
            refetchIntervalInBackground: false,

            retry: 2,
            retryDelay: 500,
          },
        },
      }),
    [addError],
  );

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};
